import { AddUserIcon, DeleteIcon } from '@approvalmax/ui';
import { Button, DateTimePicker, Flex, Form, Grid, Label, Popup, Select, Text } from '@approvalmax/ui/src/components';
import { memo } from 'react';
import { SelectUserMenuItem, UserAvatar, UserCard } from 'shared/components';
import { useGetUserWithProfileInfo } from 'shared/hooks/useGetUserWithProfileInfo';
import { useTimeZoneSelect } from 'shared/hooks/useTimeZoneSelect';

import { useSetDelegatePopupContent } from './SetDelegatePopupContent.hooks';
import { messages } from './SetDelegatePopupContent.messages';

export const SetDelegatePopupContent = memo(() => {
    const {
        companyId,
        form,
        user,
        delegateCurrent,
        delegatesOptions,
        dateFromMinDate,
        dateFromMaxDate,
        dateToMinDate,
        handleSubmit,
        handleDelegateRemove,
        disabled,
        progress,
    } = useSetDelegatePopupContent();

    const { getUserWithProfileInfo, loading } = useGetUserWithProfileInfo(delegateCurrent);

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={messages.title}
                progress={progress}
                actions={
                    <Button size='medium' color='blue80' type='submit' title={messages.buttonDoneTitle}>
                        {messages.buttonDone}
                    </Button>
                }
            />

            <Popup.Body spacing='32' className='fs-mask'>
                <Flex direction='column' alignItems='start' spacing='24'>
                    <Text font='body'>
                        {messages.contentDescriptionText({
                            name: (
                                <Text font='body' fontWeight='bold' as='span'>
                                    {user?.displayName || ''}
                                </Text>
                            ),
                            br: <br />,
                        })}
                    </Text>

                    <Text font='body' as='span'>
                        {messages.timezoneHintText({
                            pleaseNote: <b>{messages.pleaseNoteText}</b>,
                        })}
                    </Text>

                    <Grid gridTemplateColumns='384px auto' gridTemplateRows='auto auto' gap={24}>
                        {delegateCurrent ? (
                            <div>
                                <Label size='small'>{messages.selectDelegateText}</Label>

                                <Grid gridTemplateColumns='auto max-content' gridAutoRows='52px' gap={2}>
                                    <UserCard getUser={getUserWithProfileInfo} loading={loading}>
                                        <Button noPadding noUppercase icon variant='text' color='blue100'>
                                            <Flex alignItems='center' spacing='8' wrap={false}>
                                                <UserAvatar size={40} user={delegateCurrent} />

                                                <Text font='body' ellipsis={1} data-qa='selected-delegate-name'>
                                                    {delegateCurrent.displayName}
                                                </Text>
                                            </Flex>
                                        </Button>
                                    </UserCard>

                                    <Button
                                        icon
                                        noPadding
                                        color='red40'
                                        data-qa='delete-selected-delegate'
                                        onClick={handleDelegateRemove}
                                    >
                                        <DeleteIcon />
                                    </Button>
                                </Grid>
                            </div>
                        ) : (
                            <Select
                                autocomplete
                                name='delegateId'
                                size='large'
                                label={messages.selectDelegateText}
                                placeholder={messages.selectDelegatePlaceholderText}
                                items={delegatesOptions}
                                customMenuItem={({ key, ...item }) => <SelectUserMenuItem key={key} {...item} />}
                                startIcon={<AddUserIcon size={20} />}
                            />
                        )}

                        <div />

                        <Select
                            name='timeZone'
                            size='large'
                            dropdownWidth='max-content'
                            clearable={false}
                            disabled={disabled}
                            label={messages.timeZoneTitle}
                            placeholder={messages.timeZonePlaceholder}
                            {...useTimeZoneSelect(companyId)}
                        />

                        <Grid gridTemplateColumns='1fr 1fr' gap={8}>
                            <DateTimePicker
                                name='dateFrom'
                                size='large'
                                required={Boolean(delegateCurrent?.id)}
                                disabled={disabled}
                                label={messages.startDateColumnTitle}
                                placeholder={messages.startDateColumnTitle}
                                minDate={dateFromMinDate}
                                maxDate={dateFromMaxDate}
                            />

                            <DateTimePicker
                                name='dateTo'
                                size='large'
                                disabled={disabled}
                                label={messages.endDateColumnTitle}
                                placeholder={messages.endDateColumnTitle}
                                minDate={dateToMinDate}
                            />
                        </Grid>
                    </Grid>
                </Flex>
            </Popup.Body>
        </Form>
    );
});

SetDelegatePopupContent.displayName = 'SetDelegatePopupContent';
